import React, { useEffect, useRef, useState } from "react";
import { Film, Calendar, Trash2, Plus, Upload, Loader2, Clock, Tv, Radio, Zap } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const CHANNELS = ["main", "news", "music", "kids", "sports"];
const KINDS = ["commercial", "live_stream", "video"];

/**
 * CommercialsAdminTab — admin UI for:
 *   (1) Uploading / managing commercial clips
 *   (2) Scheduling slots onto the TV-Guide grid
 *   (3) Nuking seed / test data in one click (keeps real user accounts + uploads)
 */
export default function CommercialsAdminTab() {
  const [commercials, setCommercials] = useState([]);
  const [slots, setSlots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));

  const fileRef = useRef(null);
  const [newComm, setNewComm] = useState({ title: "", advertiser: "", duration_seconds: 30 });
  const [uploaded, setUploaded] = useState(null); // {storage_path, video_url}

  const [newSlot, setNewSlot] = useState({
    kind: "commercial", ref_id: "", title: "", channel: "main",
    start_hhmm: "18:00", duration_min: 5,
  });

  const load = async () => {
    setLoading(true);
    try {
      const start = new Date(day + "T00:00:00Z").toISOString();
      const end = new Date(day + "T23:59:59Z").toISOString();
      const [c, s] = await Promise.all([
        api.get("/commercials?active=false"),
        api.get(`/schedule/slots?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
      ]);
      setCommercials(c.data || []);
      setSlots(s.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [day]);

  const pickVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 500 * 1024 * 1024) return toast.error("Clip too large (max 500MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded({ storage_path: data.storage_path, video_url: `/api/files/${data.storage_path}` });
      toast.success("Clip uploaded — now save the commercial below");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const saveCommercial = async (e) => {
    e.preventDefault();
    if (!uploaded) return toast.error("Upload a video first");
    if (!newComm.title.trim()) return toast.error("Title required");
    try {
      await api.post("/commercials", {
        ...newComm,
        duration_seconds: parseInt(newComm.duration_seconds, 10) || 30,
        video_url: uploaded.video_url,
        storage_path: uploaded.storage_path,
      });
      toast.success("Commercial saved");
      setNewComm({ title: "", advertiser: "", duration_seconds: 30 });
      setUploaded(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delCommercial = async (id) => {
    if (!window.confirm("Delete this commercial? Any scheduled slots using it will also be removed.")) return;
    try { await api.delete(`/commercials/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const scheduleSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.ref_id) return toast.error("Pick what to schedule");
    const [hh, mm] = newSlot.start_hhmm.split(":").map(Number);
    const start = new Date(day + `T${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}:00`);
    const end = new Date(start.getTime() + (newSlot.duration_min * 60000));
    try {
      await api.post("/schedule/slots", {
        kind: newSlot.kind,
        ref_id: newSlot.ref_id,
        title: newSlot.title || (commercials.find((c) => c.id === newSlot.ref_id)?.title || "Slot"),
        channel: newSlot.channel,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
      });
      toast.success("Slot scheduled");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delSlot = async (id) => {
    try { await api.delete(`/schedule/slots/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const purgeSeed = async () => {
    if (!window.confirm("Delete all seed/test live streams, stories, hero slides, announcements, and demo videos? Real user accounts + real uploaded videos are kept.")) return;
    try {
      const { data } = await api.post("/admin/purge-seed");
      toast.success(`Purged: ${JSON.stringify(data.summary)}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="commercials-admin-tab" className="space-y-8">
      {/* Purge seed data */}
      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <div>
          <div className="flex items-center gap-2 text-red-400 font-bold text-sm"><Zap className="w-4 h-4" /> Seed / test data cleanup</div>
          <p className="text-white/70 text-xs mt-1">One-click purge of demo streams, stories, hero slides, and demo videos. Keeps every real user account and every real uploaded video.</p>
        </div>
        <button onClick={purgeSeed} data-testid="admin-purge-seed-btn" className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5 shrink-0">
          <Trash2 className="w-3.5 h-3.5" /> Purge seed data
        </button>
      </div>

      {/* Commercials library */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Film className="w-4 h-4 text-tv-accent" />
          <h2 className="font-display font-bold text-xl text-white">Commercials library</h2>
          <span className="text-white/40 text-xs">{commercials.length} clips</span>
        </div>

        <form onSubmit={saveCommercial} className="rounded-xl border border-white/10 bg-white/5 p-4 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex items-center gap-3">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="commercial-upload-btn" className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-60">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploaded ? "Replace clip" : "Upload clip"}
            </button>
            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={pickVideo} />
            {uploaded && <span className="text-[var(--tv-accent)] text-xs font-semibold">Ready: {uploaded.storage_path.split("/").pop()}</span>}
          </div>
          <input value={newComm.title} onChange={(e) => setNewComm({ ...newComm, title: e.target.value })} placeholder="Commercial title" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" data-testid="commercial-title-input" />
          <input value={newComm.advertiser} onChange={(e) => setNewComm({ ...newComm, advertiser: e.target.value })} placeholder="Advertiser" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" data-testid="commercial-advertiser-input" />
          <input type="number" value={newComm.duration_seconds} onChange={(e) => setNewComm({ ...newComm, duration_seconds: e.target.value })} placeholder="Duration (seconds)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
          <button type="submit" data-testid="commercial-save-btn" className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-4 py-2 rounded-full flex items-center justify-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Save commercial</button>
        </form>

        {loading ? <div className="text-white/40 mt-3 text-sm">Loading…</div> : (
          <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {commercials.map((c) => (
              <div key={c.id} data-testid={`commercial-${c.id}`} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                <video src={c.video_url} controls className="w-full aspect-video bg-black" />
                <div className="p-3">
                  <div className="text-white font-semibold text-sm truncate">{c.title}</div>
                  <div className="text-white/50 text-xs truncate">{c.advertiser || "Unsponsored"} · {c.duration_seconds}s · {c.play_count} plays</div>
                  <button onClick={() => delCommercial(c.id)} data-testid={`commercial-delete-${c.id}`} className="mt-2 text-red-400 hover:text-red-300 text-xs font-semibold flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
            {commercials.length === 0 && <div className="text-white/40 text-sm">No commercials yet. Upload one above.</div>}
          </div>
        )}
      </section>

      {/* Schedule */}
      <section>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Calendar className="w-4 h-4 text-tv-accent" />
          <h2 className="font-display font-bold text-xl text-white">TV Guide schedule</h2>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="schedule-day-picker" className="ml-3 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-white text-xs outline-none" />
          <button
            type="button"
            data-testid="schedule-seed-day"
            onClick={async () => {
              if (!window.confirm(`Auto-fill 24 hours of programming for ${day} across all 5 channels? This will replace any existing slots on that day.`)) return;
              try {
                const r = await api.post("/schedule/seed-day", { date: new Date(day + "T00:00:00Z").toISOString() });
                toast.success(`Seeded ${r.data.created} slots`);
                load();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="ml-auto bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1"
          ><Zap className="w-3.5 h-3.5" /> Auto-fill day</button>
        </div>

        <form onSubmit={scheduleSlot} className="rounded-xl border border-white/10 bg-white/5 p-4 grid sm:grid-cols-3 md:grid-cols-6 gap-3 items-end">
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold flex flex-col gap-1">Channel
            <select value={newSlot.channel} onChange={(e) => setNewSlot({ ...newSlot, channel: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm normal-case outline-none" data-testid="slot-channel-select">
              {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold flex flex-col gap-1">Kind
            <select value={newSlot.kind} onChange={(e) => setNewSlot({ ...newSlot, kind: e.target.value, ref_id: "" })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm normal-case outline-none" data-testid="slot-kind-select">
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="sm:col-span-2 text-[10px] uppercase tracking-wider text-white/60 font-bold flex flex-col gap-1">{newSlot.kind === "commercial" ? "Pick commercial" : `Paste ${newSlot.kind} ID`}
            {newSlot.kind === "commercial" ? (
              <select value={newSlot.ref_id} onChange={(e) => setNewSlot({ ...newSlot, ref_id: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm normal-case outline-none" data-testid="slot-ref-select">
                <option value="">— choose —</option>
                {commercials.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            ) : (
              <input value={newSlot.ref_id} onChange={(e) => setNewSlot({ ...newSlot, ref_id: e.target.value })} placeholder="id" className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm normal-case outline-none" data-testid="slot-ref-input" />
            )}
          </label>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold flex flex-col gap-1">Start time
            <input type="time" value={newSlot.start_hhmm} onChange={(e) => setNewSlot({ ...newSlot, start_hhmm: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm normal-case outline-none" data-testid="slot-start-time" />
          </label>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold flex flex-col gap-1">Duration (min)
            <input type="number" min="1" max="720" value={newSlot.duration_min} onChange={(e) => setNewSlot({ ...newSlot, duration_min: parseInt(e.target.value, 10) || 5 })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm outline-none" data-testid="slot-duration-input" />
          </label>
          <button type="submit" data-testid="slot-save-btn" className="sm:col-span-3 md:col-span-1 bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-4 py-2 rounded-full flex items-center justify-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Schedule</button>
        </form>

        <div className="mt-4 space-y-2">
          {slots.length === 0 && <div className="text-white/40 text-sm" data-testid="schedule-empty">No slots on {day}. Add one above or view the live TV Guide at <a href="/tv-guide" className="text-tv-accent underline">/tv-guide</a>.</div>}
          {slots.map((s) => {
            const Icon = ({ commercial: Film, live_stream: Radio, video: Tv }[s.kind] || Clock);
            return (
              <div key={s.id} data-testid={`schedule-slot-${s.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="w-10 h-10 rounded-lg bg-black/40 flex items-center justify-center text-tv-accent"><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-bold truncate">{s.title}</div>
                  <div className="text-white/50 text-xs">
                    {new Date(s.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} → {new Date(s.end_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {s.channel} · {s.kind.replace("_", " ")}
                  </div>
                </div>
                <button onClick={() => delSlot(s.id)} data-testid={`schedule-slot-delete-${s.id}`} className="text-red-400 hover:text-red-300 text-xs font-semibold flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
